import React from "react";

import { css, SerializedStyles } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { ImageWithFocalPoint } from "../_shared-components/image-related/ImageWithFocalPoint";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { CSS_MQ } from "../../styles/breakpoints";
import styled from "@emotion/styled";
import { OverlappingImagesTextElement } from "./subcomponents/OverlappingImagesTextElement";
import { ContentfulComponentCMSHook } from "@swo/shared";

import { FONTS, fontSize } from "../../styles/fonts";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { OverlappingImagesType } from "@swo/storefront";
import { OverlappingImagesVideoElement } from "./subcomponents/OverlappingImagesVideoElement";
import { T } from "../T";

const containerStyle = css`
  display: grid;

  ${CSS_MQ.atMostSm} {
    grid-row-gap: 2px;
  }

  ${CSS_MQ.atLeastMd} {
    grid-template-columns: 6fr 9fr 5fr 3fr;
    grid-template-rows: 110px auto auto auto;
  }

  ${CSS_MQ.atLeastLg} {
    grid-template-columns: 8fr 5fr 4fr 3fr;
    grid-template-rows: 70px auto auto auto;
  }
`;

const videoWrapperStyles = css`
  background-color: ${CSS_COLORS.Black};

  ${CSS_MQ.atLeastMd} {
    background-color: ${CSS_COLORS.White};
  }
`;

export const OverlappingImagesComponent: ReactTFC<OverlappingImagesType & ContentfulComponentCMSHook> = props => {
  function renderSecondElement() {
    switch (props.secondElement.__typename) {
      case "UtilityImageWithFocalPoint":
        return (
          <ImageWithFocalPoint
            assetId={props.secondElement.image.sys.id}
            {...props.secondElement}
            attachStyles={css`
              position: relative;
            `}
            requestedWidth={600}
            attachPictureStyles={css`
              display: flex;
              width: 100%;
              height: 100%;
            `}
          />
        );
      case "UtilityVideo":
        return (
          <div css={videoWrapperStyles}>
            <OverlappingImagesVideoElement video={props.secondElement} />
          </div>
        );
      default:
        return null;
    }
  }

  function renderThirdElement() {
    switch (props.thirdElement.__typename) {
      case "UtilityImageWithFocalPoint":
        return (
          <ImageWithFocalPoint
            assetId={props.thirdElement.image.sys.id}
            {...props.thirdElement}
            attachStyles={css`
              position: relative;
            `}
            requestedWidth={600}
            attachPictureStyles={css`
              display: flex;
              width: 100%;
              height: 100%;
            `}
          />
        );
      case "UtilityVideo":
        return (
          <div css={videoWrapperStyles}>
            <OverlappingImagesVideoElement video={props.thirdElement} />
          </div>
        );
      case "ComponentOverlappingImagesTextElement":
        return <OverlappingImagesTextElement {...props.thirdElement} />;
      default:
        return null;
    }
  }

  return (
    <div
      css={css`
        display: flex; // This is to fix Safari's rendering grid container (below) and overflowing its elements
      `}
    >
      <OverlappingImagesComponentContainer
        width={props.width}
        className={getComponentCMSClassname(props?.sys?.id)}
        attachStyles={containerStyle}
      >
        <div
          css={css`
            height: 100%;

            ${CSS_MQ.atLeastMd} {
              grid-row: 1 / span 3;
              grid-column: 1 / span 2;
            }
          `}
          className={"_image"}
        >
          <ImageWithFocalPoint
            assetId={props.firstElement.image.sys.id}
            {...props.firstElement}
            attachStyles={css`
              position: relative;
            `}
            requestedWidth={900}
            attachPictureStyles={css`
              display: flex;
              width: 100%;
              height: 100%;
            `}
          />
        </div>

        <div
          className={"_image"}
          css={css`
            ${CSS_MQ.atLeastMd} {
              grid-row: 2 / span 1;
              grid-column: 3 / span 2;
            }

            height: 100%;
          `}
        >
          {renderSecondElement()}
        </div>
        <div
          className={"_image"}
          css={css`
            ${CSS_MQ.atLeastMd} {
              grid-row: 3 / span 2;
              grid-column: 2 / span 2;
            }
            z-index: 1;
          `}
        >
          {renderThirdElement()}
        </div>
        {props.authors.length ? (
          <div
            css={css`
              grid-row: 4 / span 1;
              grid-column: 1 / span 1;

              z-index: 1;
            `}
          >
            <div
              css={css`
                margin-left: 20px;
                margin-top: 20px;
                margin-bottom: 20px;

                ${CSS_MQ.atLeastMd} {
                  margin-left: 64px;
                  margin-bottom: 30px;
                }
              `}
            >
              <AuthorsText>
                <T>{props.photographyLabel}</T>
                {": " + props.authors.join(", ")}
              </AuthorsText>
            </div>
          </div>
        ) : null}
      </OverlappingImagesComponentContainer>
    </div>
  );
};

const OverlappingImagesComponentContainer = (props: {
  children: React.ReactNode;
  className: string | undefined;
  attachStyles?: SerializedStyles;
  width: "full" | "content";
}) => {
  switch (props.width) {
    case "content":
      return (
        <ContentContainer className={props.className} attachStyles={props.attachStyles}>
          {props.children}
        </ContentContainer>
      );
    case "full":
      return (
        <div className={props.className} css={props.attachStyles}>
          {props.children}
        </div>
      );

    default:
      return null;
  }
};

const AuthorsText = styled.span`
  ${FONTS.SwarovskiFutura300};
  ${fontSize.x2};

  color: ${CSS_COLORS.Grey3};

  margin-top: 10px;
  ${CSS_MQ.atLeastMd} {
    margin-top: 15px;
  }
  display: block;
`;
